'use client'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './styles.module.scss'
import cls from 'classnames'
import _ from 'lodash'

export type THorizontalProps = {
  scrollStep?: number
  children: React.ReactNode
  externalScrollLeft?: number
}

export default function ScrollableContainer(props: THorizontalProps) {
  const [scrollLeft, setScrollLeft] = useState(0)
  const [leftDisabled, setLeftDisabled] = useState(true)
  const [rightDisabled, setRightDisabled] = useState(true)
  const contentElRef = useRef<HTMLDivElement>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const { scrollStep = 150, externalScrollLeft } = props

  //当尺寸变化时（不可见到可见），更新状态
  function updateDisabledStates() {
    if (contentElRef.current) {
      const scrollWidth = contentElRef.current.scrollWidth
      const clientWidth = contentElRef.current.clientWidth
      setLeftDisabled(scrollLeft === 0)
      setRightDisabled(scrollLeft === scrollWidth - clientWidth)
    }
  }

  const throttledHandleResize = useCallback(_.throttle(updateDisabledStates, 200), [updateDisabledStates])

  useEffect(() => {
    window.addEventListener('resize', throttledHandleResize)

    return () => {
      window.removeEventListener('resize', throttledHandleResize)
    }
  }, [])

  useEffect(() => {
    updateDisabledStates()
  }, [scrollLeft, props.children?.length])

  useEffect(() => {
    const handleScroll = () => {
      if (contentElRef.current) {
        setScrollLeft(contentElRef.current.scrollLeft)
      }
    }
    if (contentElRef.current) {
      contentElRef.current.addEventListener('scroll', handleScroll)
    }
    return () => {
      if (contentElRef.current) {
        contentElRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    if (contentElRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        updateDisabledStates()
      })
      resizeObserverRef.current.observe(contentElRef.current)
    }
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
    }
  }, [])

  // 监听外部传入的 scrollLeft 值变化
  useEffect(() => {
    if (contentElRef.current && typeof externalScrollLeft === 'number') {
      contentElRef.current.scrollTo({
        left: externalScrollLeft,
        behavior: 'smooth',
      })
      setScrollLeft(externalScrollLeft)
    }
  }, [externalScrollLeft])

  const handleScrollLeft = () => {
    if (contentElRef.current) {
      const containerWidth = contentElRef.current.clientWidth
      const dynamicStep = containerWidth > 300 ? scrollStep : containerWidth * 0.3
      const newScrollLeft = Math.max(0, contentElRef.current.scrollLeft - dynamicStep)
      contentElRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      })
      setScrollLeft(newScrollLeft)
    }
  }

  const handleScrollRight = () => {
    if (contentElRef.current) {
      const containerWidth = contentElRef.current.clientWidth
      const dynamicStep = containerWidth > 300 ? scrollStep : containerWidth * 0.3
      const newScrollLeft = Math.min(
        contentElRef.current.scrollWidth - containerWidth,
        contentElRef.current.scrollLeft + dynamicStep
      )
      contentElRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      })
      setScrollLeft(newScrollLeft)
    }
  }

  return (
    <div className={cls(styles['scrollable-container'], 'hide-phone')}>
      <div className={styles.content} ref={contentElRef}>
        {props.children}
      </div>
      {!leftDisabled && (
        <button
          className={cls(styles['scroll-button'], styles['left'])}
          onClick={handleScrollLeft}
          disabled={leftDisabled}
        >
          <i className="iconn-jiantou-zuo iconfont"></i>
        </button>
      )}
      {!rightDisabled && (
        <button
          className={cls(styles['scroll-button'], styles['right'])}
          onClick={handleScrollRight}
          disabled={rightDisabled}
        >
          <i className="iconn-jiantou-you iconfont"></i>
        </button>
      )}
    </div>
  )
}
